import React, { Suspense, useEffect, useMemo, useState } from "react";
import { Box } from "@material-ui/core";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import { getCard } from "./CardRegistry";
import RccHeader from "./RccHeader";
import RccNavigationBar from "./RccNavigationBar";
import { appService } from "../App/app.service";
import useRccDrilldowns from "./drilldowns/useRccDrilldowns";
import RccDrilldownModals from "./drilldowns/RccDrilldownModals";
import VisibilityGate from "./VisibilityGate";
import { fetchDefaultTeamFilter, fetchFirstLevelTeamNames } from "./rccUtils";

// Grid column counts per breakpoint
const GRID_COLUMNS_MOBILE = 1;
const GRID_COLUMNS_TABLET = 2;
const GRID_COLUMNS_LAPTOP = 4;

const useStyles = makeStyles((theme) => ({
  container: {
    // Root container: handles viewport width and centering
    width: "100%",
    maxWidth: "1400px",
    margin: "0 auto",
    paddingBottom: theme.spacing(2),
    position: "relative",
    boxSizing: "border-box",
  },
  headerContainer: {
    // Mobile (xs) padding
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),

    // Tablet (sm) padding
    [theme.breakpoints.up("sm")]: {
      paddingLeft: theme.spacing(3),
      paddingRight: theme.spacing(3),
    },

    // Laptop (md) padding
    [theme.breakpoints.up("md")]: {
      paddingLeft: theme.spacing(4),
      paddingRight: theme.spacing(4),
    },

    // Desktop (lg) padding -> Go full width only on large screens
    [theme.breakpoints.up("lg")]: {
      paddingLeft: (props) => (props.isHome ? "3%" : theme.spacing(0)),
      paddingRight: (props) => (props.isHome ? "3%" : theme.spacing(0)),
    },

    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: "1.5rem",
    alignSelf: "stretch",
  },
  gridContainer: {
    // Grid layout only - width inherited from container
    display: "grid",
    gap: theme.spacing(2),
    minWidth: "auto",
    overflow: "visible",
    alignItems: "start",
    boxSizing: "border-box",

    // Mobile (xs) padding
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),

    // 1. Mobile View (xs) -> 1 Column
    [theme.breakpoints.down("xs")]: {
      gridTemplateColumns: `repeat(${GRID_COLUMNS_MOBILE}, 1fr)`,
    },

    // 2. Tablet View (sm only) -> 2 Columns
    [theme.breakpoints.only("sm")]: {
      gridTemplateColumns: `repeat(${GRID_COLUMNS_TABLET}, 1fr)`,
      gridAutoRows: "153px",
      alignItems: "stretch",
      paddingLeft: theme.spacing(3),
      paddingRight: theme.spacing(3),
    },

    // 3. Laptop View (md+) -> 4 Columns
    [theme.breakpoints.up("md")]: {
      gridTemplateColumns: `repeat(${GRID_COLUMNS_LAPTOP}, 1fr)`,
      gridAutoRows: "153px",
      alignItems: "stretch",
      paddingLeft: theme.spacing(4),
      paddingRight: theme.spacing(4),
    },

    // 4. Desktop View (lg+) -> Remove padding
    [theme.breakpoints.up("lg")]: {
      paddingLeft: (props) => (props.isHome ? "3%" : theme.spacing(0)),
      paddingRight: (props) => (props.isHome ? "3%" : theme.spacing(0)),
    },
  },
  gridItem: {
    height: "100%",
    display: "flex",
    "& > *": {
      width: "100%",
      height: "100%",
    },
  },
}));

function Index(props) {
  const classes = useStyles({ isHome: props?.isHome });
  const theme = useTheme();

  // Define Breakpoints
  const isMobile = useMediaQuery(theme.breakpoints.down("xs"));
  const isTablet = useMediaQuery(theme.breakpoints.only("sm"));
  // Covers both md (Laptop) and lg (Desktop) for column logic
  const isLaptopAndUp = useMediaQuery(theme.breakpoints.up("md"));

  const [cardConfig, setCardConfig] = useState([]);
  const [loading, setLoading] = useState(true);

  const drilldowns = useRccDrilldowns();

  // Determine Max Span allowed
  let VIEW_TOTAL_COLUMNS = 1;
  if (isMobile) VIEW_TOTAL_COLUMNS = GRID_COLUMNS_MOBILE;
  else if (isTablet) VIEW_TOTAL_COLUMNS = GRID_COLUMNS_TABLET;
  else if (isLaptopAndUp) VIEW_TOTAL_COLUMNS = GRID_COLUMNS_LAPTOP;

  useEffect(() => {
    // Preload shared RCC bootstrap filters once at container level.
    // Cards/hooks then read the same Redux-backed values.
    fetchFirstLevelTeamNames();
    fetchDefaultTeamFilter();

    appService.getRccConfig().then(
      (resp) => {
        if (resp.success && Array.isArray(resp.data)) {
          setCardConfig(resp.data);
        }
        setLoading(false);
      },
      (err) => {
        console.error("Error fetching RCC config:", err);
        setLoading(false);
      },
    );
  }, []);

  // Get the pinned card (Performance Insights) and other cards
  const pinnedConfig = useMemo(
    () => cardConfig.find((config) => config.id === "RCC_Performance_Insights"),
    [cardConfig],
  );
  const otherCardsConfig = useMemo(
    () => cardConfig.filter((config) => config.id !== "RCC_Performance_Insights"),
    [cardConfig],
  );
  // Precompute stable drilldown handlers once per config set to avoid
  // passing new function references to every card on each parent render.
  const drilldownHandlers = useMemo(() => {
    const handlers = {};
    otherCardsConfig.forEach((config) => {
      handlers[config.id] = drilldowns.getDrilldownHandler(config.id);
    });
    return handlers;
  }, [otherCardsConfig, drilldowns]);

  // Calculate grid positioning for pinned card
  const getPinnedStyle = (config) => {
    if (!config) return {};
    const style = {};

    if (isMobile) {
      // Mobile: Order 4
      style.order = 4;
      style.gridColumn = "span 1";
    } else if (isTablet) {
      // Tablet (sm): Pinned Right (Col 2)
      style.gridColumn = "2 / 3";
      style.gridRow = "1 / span 3";
    } else if (isLaptopAndUp) {
      // Laptop (md+): Pinned Right (Col 4)
      style.gridColumn = "4 / 5";
      if (config.rowSpan) {
        style.gridRow = `1 / span ${config.rowSpan}`;
      }
    }
    return style;
  };

  const getOtherCardStyle = (config, index) => {
    const style = {};

    if (isMobile) {
      // Mobile ordering
      if (index < 3) {
        style.order = index + 1;
      } else {
        style.order = index + 2;
      }
      style.gridColumn = "span 1";
    } else {
      // Tablet & Laptop Logic
      const requestedSpan = config.columnSpan || 1;
      const actualSpan = Math.min(requestedSpan, VIEW_TOTAL_COLUMNS);

      style.gridColumn = `span ${actualSpan}`;
      style.gridRow = `span ${config.rowSpan || 1}`;
    }

    return style;
  };

  const renderCardFallback = (title = "Card") => (
    <Box
      style={{
        height: "100%",
        minHeight: "120px",
        border: "1px solid #E0E0E0",
        borderRadius: "8px",
        padding: "12px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "#FFFFFF",
        color: "#616161",
      }}
    >
      Loading {title}...
    </Box>
  );

  const renderCard = (config, extraProps = {}) => {
    const CardComponent = getCard(config.id);
    if (!CardComponent) return null;

    return (
      // Each card chunk is lazy-loaded independently; fallback keeps the grid stable
      // while the card module downloads and initializes.
      <Suspense fallback={renderCardFallback(config.title)}>
        <CardComponent
          title={config.title}
          redirectToDashboard={config.redirectToDashboard}
          {...extraProps}
        />
      </Suspense>
    );
  };

  return (
    <Box className={classes.container}>
      {/* Header */}
      <Box className={classes.headerContainer}>
        <RccHeader />
        <RccNavigationBar dashboardLabel="Revenue Command Center" />
      </Box>

      {/* Grid Container */}
      <div className={classes.gridContainer}>
        {loading ? (
          <Box
            style={{
              gridColumn: "1 / -1",
              display: "flex",
              justifyContent: "center",
              padding: "40px",
            }}
          >
            Loading Configuration...
          </Box>
        ) : (
          <>
            {/* Render pinned card first to establish its position */}
            {pinnedConfig &&
              (() => {
                return (
                  <div
                    key={pinnedConfig.id}
                    className={classes.gridItem}
                    style={getPinnedStyle(pinnedConfig)}
                  >
                    {renderCard(pinnedConfig)}
                  </div>
                );
              })()}

            {/* Render other cards - they auto-place and wrap around the pinned card */}
            {otherCardsConfig.map((config, index) => {
              // Mount only first row immediately; defer lower rows until near viewport
              // so card effects/data fetches don't all start at once.
              const shouldMountImmediately = index < VIEW_TOTAL_COLUMNS;
              return (
                <div
                  key={config.id}
                  className={classes.gridItem}
                  style={getOtherCardStyle(config, index)}
                >
                  <VisibilityGate immediate={shouldMountImmediately}>
                    {renderCard(config, {
                      onDrilldown: drilldownHandlers[config.id],
                    })}
                  </VisibilityGate>
                </div>
              );
            })}
          </>
        )}
      </div>

      {/* Drilldown modals */}
      <RccDrilldownModals {...drilldowns} />
    </Box>
  );
}

export default Index;
